import type { Dictionary } from './fr';

/**
 * Dictionnaire ESPAGNOL.
 *
 * ⚠️ Traduction automatique relue par IA (Claude, 17/10/2026), pas par un locuteur natif.
 *
 * Registre : tutoiement neutre (« tú »), sans « vosotros » ni régionalisme, lisible en Espagne comme
 * en Amérique latine. Boutons et titres d'écran à l'infinitif (« Optimizar mi CV »).
 * Glossaire : alternance → « alternancia » (contrat : « Alternancia ») ; école → « escuela » ;
 * CFA → sigle gardé, « CFA (centros de formación de aprendices) » ; CV → « CV » ; lettre de motivation → « carta de presentación » ;
 * entretien → « entrevista » ; offre → « oferta » ; fiche de poste → « descripción del puesto » ;
 * annonce → « anuncio » ; notation → « evaluación » ; candidature IA → « candidatura con IA » ;
 * CDI / CDD → sigle toujours gardé, suivi d'une glose : « CDI (indefinido) » / « CDD (temporal) » ; stage → « Prácticas » ; formule (coaching) → « modalidad » ;
 * créneau → « horario » ; permis B → « permiso de conducir (B) ». Plans Free / Plus / Pro gardés tels quels.
 */
export const es: Dictionary = {
  common: {
    back: 'Volver',
    scoreSur100: 'sobre 100',
    cancel: 'Cancelar',
    save: 'Guardar',
    retry: 'Reintentar',
    delete: 'Eliminar',
    ok: 'Entendido',
  },

  a11y: {
    close: 'Cerrar',
    removeLanguage: 'Quitar el idioma',
    removeBullet: 'Quitar el punto',
    removeProject: 'Quitar el proyecto',
    removeEducation: 'Quitar la formación',
    removeExperience: 'Quitar la experiencia',
    removeTag: 'Quitar %{tag}',
    useful: 'Útil',
    notUseful: 'No es útil',
    listen: 'Escuchar este mensaje',
    notifications: 'Notificaciones',
    settings: 'Ajustes',
    changePhoto: 'Cambiar la foto de perfil',
    changeSearchTarget: 'Cambiar tu objetivo de búsqueda',
    importPhoto: 'Importar una foto',
    score: 'Puntuación: %{score} de 100',
  },

  nav: {
    carriere: 'Carrera',
    offers: 'Ofertas',
    news: 'News',
    profile: 'Perfil',
    aiAction: 'Acción IA',
  },

  home: {
    content: {
      seeAll: 'Ver todo',
      new: 'NUEVO',
      duration: '%{minutes} min',
      play: 'Escuchar',
      types: {
        podcast: 'Podcast AlternJob',
        tip: 'El consejo del día',
        event: 'No te lo pierdas',
        success: 'Lo lograron',
        market: 'Mercado y ofertas',
        masterclass: 'Masterclass',
        challenge: 'Reto',
        news: 'Novedades en Horméia',
      },
      sample: {
        title: 'IA y selección de personal: lo que realmente cambia en 2024',
        description: 'Consejos, testimonios y tendencias del mercado laboral.',
      },
    },
  },


  carriere: {
    title: 'Carrera',
    headline: 'Conviértete en el candidato\nal que los reclutadores',
    headlineAccent: 'llaman.',
    position: 'Tarjeta %{index} de %{total}',
    cv: {
      title: 'CV ATS',
      description:
        'Crea un CV profesional, optimizado para superar los filtros y captar la atención de los reclutadores.',
      cta: 'Analizar mi CV',
    },
    linkedin: {
      title: 'LinkedIn',
      description:
        'Crea un perfil profesional y aumenta tu visibilidad ante reclutadores y empresas.',
      cta: 'Mejorar mi perfil',
    },
    letter: {
      title: 'Carta de presentación',
      description: 'Redacta cartas impactantes y personalizadas para cada candidatura.',
      cta: 'Escribir mi carta',
    },
    interview: {
      title: 'Entrevista IA',
      description: 'Practica y supera todas tus entrevistas.',
      cta: 'Practicar',
    },
    // Page Carrière sur grand écran : panneau « prochain pas », chiffres et état de chaque outil.
    tableau: {
      prochainPas: 'Tu próximo paso para conseguir entrevistas',
      premierPas: 'Empieza por saber dónde estás',
      conseilParDefaut: 'Tu CV aún puede mejorar: abre el detalle para ver las correcciones.',
      sansScore: 'Sube tu CV: recibes una puntuación ATS y las correcciones que importan, en segundos.',
      corriger: 'Corregir mi CV',
      noter: 'Puntuar mi CV',
      chiffreCv: { one: 'CV guardado', other: 'CV guardados' },
      chiffreLettres: { one: 'carta escrita', other: 'cartas escritas' },
      chiffreCorrections: { one: 'corrección sugerida', other: 'correcciones sugeridas' },
      statutCvAucun: 'Aún no hay CV',
      statutCv: { one: '%{count} CV', other: '%{count} CV' },
      statutScore: 'puntuación %{score}',
      statutLettreAucune: 'Aún no hay cartas',
      statutLettres: { one: '%{count} carta escrita', other: '%{count} cartas escritas' },
      statutLinkedin: 'Auditoría en un minuto',
      statutInterview: 'Reclutador o manager',
    },
  },

  offers: {
    title: 'Ofertas',
    subtitle: 'Pronto, ofertas seleccionadas según tu perfil y tus búsquedas.',
    soonTitle: 'Nueva función en construcción',
    soonBody:
      'Estamos preparando una selección de ofertas de alternancia (estudios + contrato en empresa) y de primer empleo que encajen con tu CV y con lo que buscas. Mientras tanto, puedes trabajar tu CV, tu perfil de LinkedIn y tus cartas desde la pestaña Carrera.',
  },

  offres: {
    franceUniquement: 'Las ofertas son solo para puestos en Francia y están redactadas en francés.',
    titre: 'Ofertas',
    sousTitre: 'Elegidas según tus criterios',
    credits: { zero: 'No te quedan candidaturas con IA este mes', one: '%{count} candidatura con IA restante', other: '%{count} candidaturas con IA restantes' },
    segments: { pourToi: 'Para ti', sauvegardees: 'Guardadas' },
    modifierCriteres: 'Editar mis criterios',
    sources: { france_travail: 'France Travail', lba: 'La bonne alternance', adzuna: 'Jobs by Adzuna' },
    contrats: { alternance: 'Alternancia', stage: 'Prácticas', cdi: 'CDI (indefinido)', cdd: 'CDD (temporal)', autre: 'Otro contrato' },
    contratsEnLigne: { alternance: 'alternancia', stage: 'prácticas', cdi: 'CDI (indefinido)', cdd: 'CDD (temporal)', autre: 'otro contrato' },
    rythmes: {
      '3j2j': '3 d / 2 d',
      '4j1j': '4 d / 1 d',
      '1sem1sem': '1 sem / 1 sem',
      '2sem2sem': '2 sem / 2 sem',
      '3sem1sem': '3 sem / 1 sem',
      '2j3j': '2 d / 3 d',
    },
    rythmesComplets: {
      '3j2j': '3 días en la empresa, 2 días en la escuela',
      '4j1j': '4 días en la empresa, 1 día en la escuela',
      '1sem1sem': '1 semana en la empresa, 1 semana en la escuela',
      '2sem2sem': '2 semanas en la empresa, 2 semanas en la escuela',
      '3sem1sem': '3 semanas en la empresa, 1 semana en la escuela',
    },
    dureesStage: { '2': '2 meses', '3': '3 meses', '4': '4 meses', '5': '5 meses', '6': '6 meses' },
    dureesCdd: { lt6: 'Menos de 6 meses', '6a12': 'De 6 a 12 meses', '12plus': '1 año o más' },
    rayonKm: '%{km} km',
    preferences: {
      titre: 'Tus criterios',
      sousTitre: 'Buscamos según lo que elijas aquí.',
      contratMulti: 'puedes elegir varios',
      peuImporte: 'Da igual',
      cdiSeul: 'En un contrato indefinido (CDI) no hay nada más que elegir.',
      verifier: 'Revisa los campos señalados arriba.',
      metier: 'Puesto buscado',
      metierPlaceholder: 'Ej.: responsable de comunicación digital',
      contrat: 'Tipo de contrato',
      ville: 'Ciudad',
      villePlaceholder: 'Ej.: Lyon',
      rayon: 'Radio',
      rythme: 'Ritmo de alternancia',
      rythmeAide: 'Empresa / escuela',
      dureeStage: 'Duración de las prácticas',
      dureeCdd: 'Duración del contrato temporal',
      dureeAide: 'Se descartan las ofertas que indican otra duración.',
      reponsesTitre: 'Respuestas clave, nunca inventadas',
      permis: 'Permiso de conducir (B)',
      oui: 'Sí',
      non: 'No',
      aRenseigner: 'Por completar',
      disponibilite: 'Disponible a partir del',
      disponibilitePlaceholder: 'DD/MM/AAAA',
      voir: 'Ver mis ofertas',
      metierRequis: 'Indica el puesto que buscas.',
      villeRequise: 'Indica tu ciudad.',
      villeIntrouvable: 'Ciudad no encontrada en Francia. Revisa la ortografía o elige una ciudad francesa.',
      villeRetenue: 'Ciudad entendida: %{commune}. Si no es la correcta, corrígela; si lo es, vuelve a confirmar.',
      contratRequis: 'Elige al menos un tipo de contrato.',
      disponibiliteInvalide: 'Escribe la fecha con el formato DD/MM/AAAA.',
      erreurEnregistrement: 'No se pudieron guardar tus criterios. Inténtalo de nuevo.',
      chargement: 'Cargando tus criterios…',
      retour: 'Volver a las ofertas',
    },
    raisons: {
      contrat: 'Contrato: %{valeur}',
      contrat_different: 'Contrato distinto al que buscas',
      distance: 'A %{valeur} km de tu ciudad',
      distance_loin: 'A %{valeur} km, fuera de tu radio',
      distance_inconnue: 'Lugar de trabajo no indicado',
      distance_rayon: 'Dentro de tu radio de %{valeur} km',
      rythme: 'Ritmo %{valeur}, como quieres',
      rythme_different: 'Ritmo %{valeur}, distinto al tuyo',
      rythme_absent: 'Ritmo de alternancia no indicado',
      metier: 'Puesto parecido al tuyo: «%{valeur}»',
      metier_partiel: 'Puesto en parte parecido: «%{valeur}»',
      metier_rome: 'Misma familia profesional que el tuyo (código ROME %{valeur})',
      metier_eloigne: 'Puesto alejado del que buscas',
      metier_sans_citation: 'Corresponde al puesto que buscas',
      metier_partiel_sans_citation: 'Parecido al puesto que buscas',
      permis_a_renseigner: 'Se pide permiso de conducir (B): por completar',
      permis_manquant: 'Se pide permiso de conducir (B)',
    },
    carte: {
      pourquoi: 'Por qué encaja',
      publiee: { one: '%{source} · publicada hace %{jours} d', other: '%{source} · publicada hace %{jours} d' },
      publieeAujourdhui: '%{source} · publicada hoy',
      voirDetail: 'Ver detalle',
      voirOffreComplete: 'Ver la oferta completa',
      contratDuree: '%{contrat} · %{duree}',
      dureeMois: { one: '%{count} mes', other: '%{count} meses' },
      salaire: 'Salario: %{valeur}',
      entrepriseInconnue: 'Empresa no indicada',
      lieuDistance: '%{lieu} · %{km} km',
      score: 'Puntuación de afinidad: %{score} de 100',
    },
    actions: {
      passer: 'Descartar esta oferta',
      sauvegarder: 'Guardar esta oferta',
      interesse: 'Me interesa esta oferta',
      aide: 'Desliza a la derecha si te interesa, a la izquierda si no',
      libelles: { passer: 'Descartar', sauvegarder: 'Guardar', interesse: 'Me interesa' },
    },
    detail: {
      voirCandidature: 'Ver mi candidatura',
      alerteMineur: 'A revisar: esta oferta parece incluir %{motifs}, prohibido o restringido para menores de 18 años.',
      motifsMineur: { nuit: 'trabajo nocturno', alcool: 'servicio de alcohol', hauteur: 'trabajo en altura' },
      fermer: 'Cerrar el detalle',
      monMatch: 'Tu afinidad',
      description: 'Descripción',
      extrait: 'Extracto del anuncio',
      extraitAide: 'Adzuna solo comparte el principio del anuncio. El texto completo está en su sitio web.',
      voirOffreComplete: 'Ver la oferta completa',
      entreprise: 'La empresa',
      infos: 'Información',
      voirOffre: 'Ver la oferta original',
      preparer: 'Preparar mi candidatura',
      source: 'Fuente: %{source}',
      majLe: 'Actualizada el %{date}',
      licence: 'Reutilización sujeta a la licencia de France Travail',
      retiree: 'El reclutador ha retirado esta oferta.',
      logo: 'Logotipo de %{entreprise}',
      sauvegardee: 'Oferta guardada',
      rubriques: {
        nature_contrat: 'Naturaleza del contrato',
        type_contrat: 'Tipo de contrato',
        duree_travail: 'Jornada laboral',
        experience: 'Experiencia',
        formations: 'Formación',
        competences: 'Competencias',
        qualites: 'Cualidades profesionales',
        savoirs: 'Herramientas',
        langues: 'Idiomas',
        permis: 'Permiso de conducir',
        salaire: 'Salario',
        secteur: 'Sector de actividad',
        qualification: 'Cualificación',
        deplacements: 'Desplazamientos',
        conditions: 'Condiciones de trabajo',
        postes: 'Número de puestos',
        accessible_th: 'Accesible para personas con discapacidad',
        contact: 'Contacto',
        agence: 'Agencia France Travail',
        duree_contrat: 'Duración del contrato',
        debut: 'Inicio del contrato',
        expiration: 'Oferta válida hasta el',
        rome: 'Código ROME',
      },
    },
    preparation: {
      parallele: 'EN PARALELO',
      motsClesTitre: 'PALABRAS CLAVE DETECTADAS EN LA OFERTA',
      motsClesAutres: '+%{count}',
      motsClesA11y: { one: '1 palabra clave detectada: %{liste}', other: '%{count} palabras clave detectadas: %{liste}' },
      motsClesCouvertsA11y: '%{couverts} de %{total} palabras clave cubiertas por tu CV: %{liste}',
      titre: 'Tu IA está preparando tu candidatura',
      pret: 'Tu candidatura está lista',
      pour: 'Para %{entreprise} · %{poste}',
      pourPoste: 'Para %{poste}',
      fermer: 'Cerrar la preparación',
      terminer: 'Cerrar',
      verifier: 'Revisar mi candidatura',
      reessayer: 'Reintentar',
      retourOffres: 'Volver a las ofertas',
      pied: 'Nada se envía sin tu aprobación',
      progression: 'Preparación de la candidatura',
      etapes: { analyse: 'Análisis de la oferta', cv: 'CV adaptado a la oferta', lettre: 'Carta de presentación', controle: 'Control de calidad' },
      attente: 'En espera',
      enCours: 'En curso…',
      echecEtape: 'Este paso ha fallado',
      motsCles: { zero: 'ninguna palabra clave', one: '1 palabra clave', other: '%{count} palabras clave' },
      questions: { zero: 'ninguna pregunta', one: '1 pregunta', other: '%{count} preguntas' },
      analyseFaite: '%{motsCles}, %{questions} detectadas',
      cvFait: { zero: 'Ninguna palabra clave integrada', one: '1 palabra clave integrada', other: '%{count} palabras clave integradas' },
      titreAligne: ', título alineado con la oferta',
      lettreFaite: 'Carta de %{count} palabras',
      controleFait: { zero: 'Nada que confirmar', one: '1 punto por confirmar', other: '%{count} puntos por confirmar' },
      couverture: 'Palabras clave cubiertas: %{couverts} de %{total}',
      erreurs: {
        quota: 'Has usado todas tus candidaturas con IA de este mes.',
        cv_manquant: 'Necesitas un CV principal para preparar una candidatura. Crea o importa tu CV en Carrera.',
        offre_retiree: 'Esta oferta ya no está publicada: no se puede preparar la candidatura.',
        profil_trop_eloigne: 'Esta oferta está demasiado lejos de tu CV para escribir una carta creíble.',
        echec: 'Un paso ha fallado. Inténtalo de nuevo: los pasos ya hechos se conservan.',
      },
    },
    avant: {
      fermer: 'Cerrar',
      naissanceTitre: 'Tu fecha de nacimiento',
      naissanceRaison: 'Sirve para adaptar el tono de tu carta y avisarte si una oferta no está permitida antes de los 18 años. Solo la das una vez.',
      naissanceFormat: 'DD/MM/AAAA',
      erreurNaissance: 'No se pudo guardar tu fecha de nacimiento. Inténtalo de nuevo.',
      continuer: 'Continuar',
      annuler: 'Cancelar',
      eloigneeTitre: 'Esta oferta está lejos de tu perfil',
      eloigneeTexte: 'Tu afinidad es de %{score} sobre 100. Puedes preparar tu candidatura de todos modos: se usará un crédito si se completa.',
      prepareQuandMeme: 'Preparar igualmente',
      nePasPreparer: 'No preparar',
    },
    relecture: {
      titre: 'Revisa tu candidatura',
      fermer: 'Cerrar la revisión',
      erreurLecture: 'No se pudo cargar tu candidatura.',
      reessayer: 'Reintentar',
      manquants: 'No aparecen en tu CV: %{liste}',
      aConfirmer: 'Revisar antes de enviar',
      raisons: {
        nombre: 'Cifra que no aparece en tu CV',
        date: 'Fecha que no aparece en tu CV',
        competence: 'Competencia que tu CV no muestra',
        a_confirmer: 'Por confirmar',
      },
      exact: 'Sí, es correcto',
      corriger: 'Corregir',
      enregistrer: 'Guardar',
      annuler: 'Cancelar',
      champCorrection: 'Tu corrección',
      confirme: 'Confirmado',
      corrige: 'Corregido',
      erreurCorrection: 'No se pudo guardar esta corrección. Inténtalo de nuevo.',
      onglets: { cv: 'CV', lettre: 'Carta', reponses: 'Respuestas' },
      legende: 'Resaltado: modificado por la IA para esta oferta.',
      formation: 'Formación',
      competences: 'Competencias',
      langues: 'Idiomas',
      objet: 'Asunto:',
      copierLettre: 'Copiar la carta',
      erreurCopie: 'No se pudo abrir el menú para compartir. Inténtalo de nuevo.',
      reponses: {
        oui: 'Sí',
        non: 'No',
        date: 'Disponible el %{date}',
        age: '%{age} años',
        aRenseigner: 'Por completar',
        aucune: 'No se ha detectado ninguna pregunta eliminatoria en esta oferta.',
      },
      exporterCv: 'Exportar el CV (PDF)',
      exporterLettre: 'Exportar la carta (PDF)',
      erreurExport: 'La exportación ha fallado. Inténtalo de nuevo.',
      voirOffre: 'Ver la oferta',
      voirOffreComplete: 'Ver la oferta completa',
      extrait: 'Preparada a partir de un extracto del anuncio: lee la oferta completa antes de enviar tu candidatura.',
    },
    etats: {
      chargement: 'Buscando tus ofertas…',
      erreurTitre: 'No se pudieron cargar las ofertas',
      erreurTexte: 'Revisa tu conexión y vuelve a intentarlo.',
      reessayer: 'Reintentar',
      videTitre: 'No hay ofertas con estos criterios',
      videTexte: 'Amplía tu radio o prueba con un puesto parecido.',
      videTexteDuree: 'Amplía tu radio, prueba con un puesto parecido o flexibiliza la duración del contrato.',
      epuiseTitre: 'Has visto todas las ofertas disponibles',
      epuiseTexte: 'Vuelve más tarde o amplía tus criterios para ver otras.',
      sourcePartielle: 'Una fuente de ofertas no responde: la lista puede estar incompleta.',
      sauvegardeesVideTitre: 'Ninguna oferta guardada',
      sauvegardeesVideTexte: 'Toca el marcador de una oferta para encontrarla aquí.',
      retiree: 'Oferta retirada',
      decisionEchec: 'No se pudo guardar tu elección. Revisa tu conexión.',
    },
  },

  news: {
    title: 'News',
    segments: { forYou: 'Para ti', favorites: 'Favoritos' },
    verified: 'Editor verificado',
    readingTime: '%{count} min de lectura',
    relative: {
      today: 'Hoy',
      yesterday: 'Ayer',
      days: { one: 'Hace %{count} d', other: 'Hace %{count} d' },
      weeks: { one: 'Hace %{count} sem.', other: 'Hace %{count} sem.' },
      months: { one: 'Hace %{count} mes', other: 'Hace %{count} meses' },
      years: { one: 'Hace %{count} año', other: 'Hace %{count} años' },
    },
    like: {
      add: 'Me gusta este artículo',
      remove: 'Ya no me gusta',
      count: {
        zero: 'Ningún me gusta',
        one: '%{count} me gusta',
        other: '%{count} me gusta',
      },
      failed: 'No se pudo actualizar este me gusta.',
    },
    favorite: {
      add: 'Añadir a favoritos',
      remove: 'Quitar de favoritos',
      added: 'En favoritos',
      notAdded: 'No está en favoritos',
      failed: 'No se pudo actualizar este favorito.',
    },
    errorTitle: 'No se pudo cargar el contenido',
    errorBody: 'Revisa tu conexión y vuelve a intentarlo.',
    emptyTitle: 'Aún no hay artículos',
    emptyBody: 'Los nuevos artículos aparecerán aquí en cuanto se publiquen.',
    favoritesEmptyTitle: 'Ningún favorito',
    favoritesEmptyBody: 'Toca el marcador de un artículo para encontrarlo aquí.',
    favoritesSignedOutTitle: 'Inicia sesión para guardar tus artículos',
    favoritesSignedOutBody: 'Tus favoritos están vinculados a tu cuenta y te siguen de un dispositivo a otro.',
    end: 'Estás al día.',
  },

  notifications: {
    welcome: {
      title: 'Te damos la bienvenida a Horméia 👋',
      body: 'Empieza por generar un CV optimizado para ATS y luego practica tus entrevistas con la IA. Dos pasos y conseguirás más entrevistas.',
    },
    quantify: {
      title: 'Pon cifras a tus resultados',
      body: 'En tu CV, cambia «gestión de redes sociales» por «+35 % de interacción en 3 meses». Los reclutadores y los filtros ATS recuerdan las cifras.',
    },
    followUp: {
      title: 'Haz seguimiento a los 7 días',
      body: 'Si no tienes respuesta una semana después de tu candidatura, envía un mensaje breve y educado al reclutador: un buen seguimiento suele duplicar las posibilidades de respuesta.',
    },
  },

  myCv: {
    title: 'Mi CV',
    scoreLabel: 'Tu puntuación ATS',
    scoreNone: 'Haz una primera evaluación para descubrir tu puntuación ATS.',
    scoreClean: 'No hay mejoras pendientes. Tu CV está listo.',
    scoreAdvice: {
      one: '%{count} mejora aún puede aumentar tus posibilidades.',
      other: '%{count} mejoras aún pueden aumentar tus posibilidades.',
    },
    scoreActionFirst: 'Evaluar mi CV',
    scoreActionDetail: 'Ver detalle',
    createOrImport: 'Crear o importar',
    importTitle: 'Importar mi CV',
    importSubtitle: 'PDF o Word',
    createTitle: 'Crear un CV',
    createSubtitle: 'A partir de una plantilla',
    improve: 'Mejorar mi CV',
    scoreRowTitle: 'Evaluar mi CV',
    scoreRowSubtitle: 'Puntuación ATS y prioridades',
    optimiseTitle: 'Optimizar mi CV',
    optimiseSubtitle: 'Mejora lo que ya tienes',
    myCvs: 'Mis CV',
    savedCount: {
      zero: 'Ningún CV guardado',
      one: '%{count} CV guardado',
      other: '%{count} CV guardados',
    },
    savedListOpen: 'Ver mis CV guardados',
    listSubtitle: 'Encuentra, vuelve a abrir y gestiona tus CV',
    priorityLabel: 'Corrige esto primero',
    importShort: 'Importar',
    createShort: 'Crear un CV',
    cvNote: 'CV evaluado',
    changer: 'Cambiar',
  },

  cvList: {
    title: 'Mis CV',
    subtitle: 'Encuentra, vuelve a abrir y gestiona tus CV guardados.',
    loadError: 'No se pudieron cargar tus CV. Inténtalo de nuevo.',
    emptyTitle: 'Aún no tienes ningún CV.',
    emptyText: 'Crea tu primer CV optimizado para ATS en pocos minutos.',
    emptyCta: 'Crear mi CV',
    primary: 'Principal',
    setPrimary: 'Definir como principal',
    edit: 'Editar',
    preview: 'Vista previa',
    others: 'Mis otros CV',
    score: 'Puntuación ATS %{score}',
    more: 'Más acciones',
    deleteTitle: '¿Eliminar este CV?',
    deleteBody: '«%{title}» se eliminará definitivamente.',
  },

  cvImprove: {
    title: 'Optimizar mi CV',
    subtitle: 'La IA propone cambios concretos y tú te quedas con los que quieras.',
    loading: 'La IA está analizando tu CV…',
    blocked: 'Esta función está reservada a los suscriptores.',
    notFound: 'CV no encontrado. Vuelve a la evaluación e inténtalo de nuevo.',
    saveFailed: 'No se pudo guardar.',
    reponseInvalide: 'La respuesta está incompleta. Inténtalo de nuevo en un momento.',
    defaultTitle: 'CV optimizado',
    choixQuestion: '¿Quieres importar una oferta o descripción del puesto para que tu CV encaje mejor con la oferta?',
    choixDeja: 'Evaluaste tu CV con una descripción del puesto. ¿Quieres usarla para optimizarlo?',
    choixNon: 'No, optimizar directamente',
    choixOui: 'Sí, añadir una descripción del puesto',
    choixUtiliser: 'Usar la descripción ya importada',
    ficheTitre: 'Descripción del puesto',
    ficheSousTitre: 'Tu CV se adaptará a esta oferta, sin inventar nada.',
    ficheChamp: 'CAPTURAS, PDF O TEXTO',
    ficheAide: 'Combínalos si hace falta: se leerán como una sola oferta. Máximo 5 capturas y 8 MB, PDF de 20 páginas como máximo.',
    optimiser: 'Optimizar mi CV',
    passer: 'Omitir',
    ficheUtilisee: 'Adaptado a la descripción del puesto proporcionada.',
    // Optimisation par modifications (1.2.0).
    revue: {
      titre: {
        one: '%{count} cambio propuesto',
        other: '%{count} cambios propuestos',
      },
      aide: 'Acepta o rechaza cada cambio. Tu CV no cambia hasta que los apliques.',
      aideTitre: 'Acepta o rechaza cada cambio.',
      aideSuite: 'Tu CV no cambia hasta que los apliques.',
      // Libellé SEUL, posé à côté du nombre affiché dans sa tuile (le titre complet reste `titre`).
      libelle: { one: 'cambio propuesto', other: 'cambios propuestos' },
      score: 'El CV modificado no se vuelve a evaluar: evalúalo después para conocer su puntuación ATS.',
    },
    tags: { impact: 'Impacto', action: 'Verbo de acción', keywords: 'Palabras clave', clarity: 'Claridad', title: 'Título' },
    modif: { avant: 'Antes', apres: 'Después', appliquer: 'Aplicar: %{nom}', titreCv: 'Título del CV' },
    pied: {
      retenues: 'Elegidos: %{count} de %{total}',
      appliquer: {
        one: 'Aplicar %{count} cambio',
        other: 'Aplicar %{count} cambios',
      },
      aucune: 'Ningún cambio elegido',
    },
    vide: { titre: 'Tu CV ya es sólido', detail: 'La IA no encontró ningún cambio útil que proponer. No se ha descontado nada de tu cuota.' },
    scoreInconnu: { titre: 'Puntuación sin medir', detail: 'Evalúa este CV para conocer su puntuación ATS.' },
  },

  ficheDePoste: {
    choisirOffre: 'Elegir una oferta',
    chargement: 'Cargando tus ofertas…',
    erreur: 'No se pudieron cargar tus ofertas guardadas.',
    vide: 'Ninguna oferta guardada. Guarda una oferta en la pestaña Ofertas para encontrarla aquí.',
    coller: 'Pegar el anuncio completo',
    compteur: '%{n} / %{max} caracteres',
    retirerCapture: 'Quitar la captura %{n}',
    retirerFichier: 'Quitar el archivo',
    erreurs: {
      tropDeCaptures: '%{max} capturas como máximo.',
      captureIllisible: 'No se pudo leer una captura. Elige otra imagen.',
      tropLourd: 'Las capturas superan 8 MB en total. Quita una para continuar.',
      pdfTropLong: 'Este PDF supera las 20 páginas. Deja solo la descripción del puesto.',
    },
  },

  cvScore: {
    failed: 'La evaluación ha fallado. Inténtalo de nuevo.',
    title: 'Evalúa tu CV',
    targetRoleLabel: 'Puesto objetivo',
    targetRoleHint: 'El puesto al que aspiras: la IA también evalúa cómo encaja tu CV con él.',
    targetRolePlaceholder: 'Ej. Desarrollador web en alternancia',
    offerLabel: 'Descripción del puesto',
    offerHint: 'Añade la oferta para obtener una puntuación de afinidad y consejos centrados en este puesto.',
    offerPlaceholder: 'Pega la oferta aquí…',
    offerFile: 'Oferta (PDF/Word)',
    offerCaptures: 'Capturas',
    optional: 'Opcional',
    ficheAdded: 'Añadida',
    moreAccurate: 'Para una nota más precisa',
    frise: { import: 'Importa', evalue: 'La IA evalúa', score: 'Tu puntuación' },
    dropTitle: 'Suelta tu CV',
    dropSub: 'PDF o Word. Recibes tu puntuación ATS y consejos concretos.',
    dropReady: 'CV listo para evaluar',
    dropTextReady: 'El texto de tu CV está listo.',
    dropPick: 'Elegir un archivo',
    dropChange: 'Cambiar de archivo',
    hintEmpty: 'Importa un CV para obtener tu puntuación ATS.',
    hintReady: 'Listo: tu puntuación llega en unos segundos.',
    submit: 'Evaluar mi CV',
    atsScore: 'Puntuación ATS',
    offerMatch: 'Afinidad con la oferta',
    strengths: 'PUNTOS FUERTES PARA ESTE PUESTO',
    gaps: 'DIFERENCIAS CON LA OFERTA',
    saveImported: 'Guardar en Mis CV',
    saveFailed: 'No se pudo guardar. Inténtalo de nuevo.',
    importedTitle: 'CV importado',
    improve: 'Optimizar mi CV',
    verdict: { faible: 'Hay que mejorarlo', correct: 'Correcto, mejorable', bon: 'Buen CV, unos ajustes', excellent: 'Excelente, listo para enviar' },
    resultDetail: {
      zero: 'Ninguna mejora pendiente.',
      one: '%{count} mejora aún puede aumentar tus posibilidades.',
      other: '%{count} mejoras aún pueden aumentar tus posibilidades.',
    },
    advicePrio: 'Corrige esto primero',
    rescore: 'Volver a evaluar',
    rescoreTitre: '¿Volver a evaluar?',
    rescoreMessage: 'Vuelves al formulario. Se perderá la nota mostrada.',
    rescoreAnnuler: 'Conservar la nota',
    criteres: { titre: 'Detalle por criterio', keywords: 'Palabras clave', layout: 'Formato ATS', impact: 'Resultados cuantificados', readability: 'Legibilidad' },
  },

  cvCreate: {
    questions: {
      role: { label: 'Puesto buscado', placeholder: 'Ej. Responsable de proyecto' },
      education: {
        label: 'Formación, títulos y certificaciones',
        placeholder: 'Ej. Máster en Marketing, IAE París',
      },
      experiences: {
        label: 'Experiencia profesional (prácticas, alternancias, empleos)',
        placeholder: 'Describe tu experiencia profesional',
      },
      projects: {
        label: 'Proyectos personales (propios, asociativos, side projects)',
        placeholder: 'Describe tus proyectos personales',
      },
      skills: { label: 'Competencias y herramientas', placeholder: 'Ej. SQL, SEO, Excel, Figma' },
      languages: { label: 'Idiomas' },
      interests: {
        label: 'Intereses y aficiones',
        placeholder: 'Ej. Trail, fotografía, ajedrez',
      },
      extra: {
        label: '¿Algo que añadir? (opcional)',
        placeholder: 'Campo libre, la IA lo integrará en el lugar adecuado',
      },
    },
    steps: {
      analyse: 'Análisis de tus respuestas',
      format: 'Maquetación del CV',
      ats: 'Verificación ATS',
    },
  },

  languagePicker: {
    namePlaceholder: 'Idioma',
    level: 'Nivel',
    diploma: 'Certificado (opcional)',
    diplomaPlaceholder: 'Ej. TOEFL 105 (o déjalo vacío)',
    add: 'Añadir un idioma',
  },

  editors: {
    experience: {
      titre: 'Experiencia',
      rowTitle: 'Experiencia %{n}',
      roleLabel: 'Puesto',
      end: 'Fin',
      descriptionLabel: 'Lo que hiciste',
      descriptionPlaceholder: 'Escribe sin complicarte, la IA lo reformula.',
      role: 'Nombre del puesto',
      company: 'Empresa',
      start: 'Inicio',
      current: 'Puesto actual',
      description: 'Lo que hiciste (la IA lo reformulará)',
      add: 'Añadir una experiencia',
    },
    education: {
      titre: 'Formación',
      rowTitle: 'Formación %{n}',
      degree: 'Título / nombre',
      school: 'Centro',
      date: 'Fecha',
      current: 'En curso',
      add: 'Añadir una formación',
    },
    project: {
      titre: 'Proyectos',
      rowTitle: 'Proyecto %{n}',
      descriptionLabel: 'Lo que realizaste',
      descriptionPlaceholder: 'Escribe sin complicarte, la IA lo reformula.',
      name: 'Nombre del proyecto',
      date: 'Fecha',
      current: 'En curso',
      description: 'Lo que realizaste (la IA lo reformulará)',
      add: 'Añadir un proyecto',
    },
    bullets: {
      placeholder: 'Describe un resultado o una acción',
      add: 'Añadir un punto',
    },
    template: {
      groupLabel: 'Modelo de CV',
      mono: 'Profesional',
      accent: 'Con color',
      accentColor: 'Color de acento',
      couleur: {
        lime: 'Lima',
        vert: 'Verde',
        bleu: 'Azul',
        beige: 'Beige',
        marron: 'Marrón',
        ardoise: 'Pizarra',
        marine: 'Azul marino',
      },
    },
    quantifyHint: 'Acuérdate de cuantificar tus resultados (cuántos, qué %, qué impacto). Eso mejora tu puntuación ATS.',
    searchTarget: 'Buscas…',
  },

  cvEditor: {
    before: 'ANTES',
    after: 'DESPUÉS - propuesto por la IA',
    reject: 'Rechazar',
    accept: 'Aceptar',
    improveWithAi: 'Mejorar con la IA',
    headline: 'Título del CV',
    experiences: 'Experiencia',
    projects: 'Proyectos',
    education: 'Formación',
    skills: 'Competencias',
    languages: 'Idiomas',
    interests: 'Intereses',
    headlinePlaceholder: 'Ej. AI Ambassador & Community Coordinator',
    startPlaceholder: 'Ej. ene. 2024',
    endPlaceholder: 'Ej. Actualidad',
    projectDatePlaceholder: 'Ej. mar. 2024',
    educationDatePlaceholder: 'Ej. sept. 2025',
    skillPlaceholder: 'Ej. SQL',
    languagePlaceholder: 'Ej. Inglés - C1',
    interestPlaceholder: 'Ej. Trail',
    experienceLabel: 'Experiencia',
    projectLabel: 'Proyecto',
    startLabel: 'Inicio',
    endLabel: 'Fin',
    dateLabel: 'Fecha',
    rewriteRetry: 'Inténtalo de nuevo.',
  },

  qualityCheck: {
    title: 'Lo que se ha mejorado',
    corrections: 'Correcciones',
    languagesRemoved: 'Idiomas eliminados (nivel inferior a B2)',
    skillsNotProven: 'Competencias de la oferta no demostradas',
    toValidate: 'Para que lo confirmes tú',
    removed: 'Elementos eliminados',
    recommendations: 'Recomendaciones',
  },

  linkedin: {
    sousScores: { titre: 'Detalle por sección', title: 'Titular', about: 'Acerca de', experiences: 'Experiencia' },
    kit: {
      failed: 'No se pudo generar el kit de optimización. Tu puntuación y tus prioridades se conservan.',
      retry: 'Reintentar el kit',
    },
    title: 'Auditoría de LinkedIn',
    subtitle: 'La IA lee tu perfil y te dice qué mejorar.',
    verdict: { faible: 'Perfil por reforzar', correct: 'Perfil correcto, mejorable', bon: 'Buen perfil, unos ajustes', excellent: 'Perfil excelente' },
    resultDetail: {
      zero: 'Ninguna acción prioritaria.',
      one: '%{count} acción prioritaria para mejorar.',
      other: '%{count} acciones prioritarias para mejorar.',
    },
    profilAnalyse: 'Perfil analizado',
    relancer: 'Repetir',
    relancerTitre: '¿Repetir la auditoría?',
    relancerMessage: 'Volverás al formulario. Se perderán la auditoría y el kit mostrados.',
    relancerAnnuler: 'Conservar esta auditoría',
    languageLabel: 'Idioma de la auditoría',
    languageHint: 'Tu perfil se queda en su idioma: la auditoría y el kit se redactan en este.',
    addCaptures: 'Añadir capturas',
    analyse: 'Analizar mi perfil',
    sourceConnect: 'Cuenta de LinkedIn',
    sourceShots: 'Capturas',
    capturesGras: 'Hasta 5 capturas',
    capturesSuite: ' de tu perfil, en el orden en que quieres que se lean.',
    captureRemove: 'Quitar la captura %{n}',
    profil: { titre: 'Titular', apropos: 'Acerca de', experiences: 'Experiencia' },
    promesses: { audit: 'Una auditoría detallada', kit: 'Un kit para copiar' },
    hint: { empty: 'Añade al menos una captura para iniciar la auditoría.', ready: 'Todo listo, puedes iniciar la auditoría.', connect: 'Conecta tu LinkedIn para iniciar la auditoría.', account: 'Tu perfil se lee directamente desde tu cuenta.' },
    missingKeywords: 'Palabras clave que añadir',
    priorities: 'Acciones prioritarias',
    connection: {
      secureTitle: 'Conexión segura',
      secureBody: 'Solo accedemos a la información necesaria para analizar tu perfil.',
      connected: 'LinkedIn conectado',
      connectedSub: 'Conexión segura, sin tu contraseña.',
      expired: 'La sesión de LinkedIn ha caducado. Vuelve a conectarte para reactivarla.',
      unavailable: 'La conexión con LinkedIn solo está disponible en la UE, el EEE y Suiza.',
      unknown: 'Estado de LinkedIn no disponible por el momento.',
      failed: 'No se pudo conectar con LinkedIn. Inténtalo de nuevo.',
      fallbackHint: 'Las capturas siguen funcionando.',
      eligibilityHint:
        'Disponible en la UE, el EEE y Suiza. En otros lugares, usa la pestaña Capturas — el análisis es el mismo.',
      connect: 'Conectar mi LinkedIn',
      reconnect: 'Volver a conectar',
      disconnect: 'Desconectar',
    },
    optimisation: {
      v1: 'Versión 1 (directa al reclutador)',
      v2: 'Versión 2 (palabras clave)',
      v3: 'Versión 3 (diferenciadora)',
    },
  },

  linkedinKit: {
    ready: 'Tu kit para copiar',
    readyHint: 'Toca «Copiar» en un bloque, o copia todo el kit, y pégalo en LinkedIn.',
    copy: 'Copiar',
    copied: 'Copiado',
    copyFailed: 'Error',
    copyAll: 'Copiar todo el kit',
    kitCopied: 'Kit copiado',
    kitCopyFailed: 'No se pudo copiar: selecciona el texto a mano.',
    copyLabel: 'Copiar: %{nom}',
    headline: 'Titular',
    about: 'Sección Acerca de',
    aboutShort: 'Versión corta',
    aboutFull: 'Versión completa',
    experiences: 'Experiencias clave',
    skills: 'Competencias',
    skillsPinned: 'Para destacar (5)',
    skillsExtra: 'Complementarias',
    search: 'Parámetros de búsqueda',
    searchTitles: 'Puestos',
    searchContracts: 'Contratos',
    searchLocations: 'Ubicaciones',
    searchSectors: 'Sectores',
    searchAvailability: 'Disponibilidad',
    express: 'Consejos exprés',
    photo: 'Foto',
    banner: 'Banner',
    url: 'URL',
    education: 'Formación (para pegar)',
    quality: 'Control de calidad',
    keywordsAdded: 'Palabras clave añadidas',
    languagesRemoved: 'Idiomas eliminados (inferior a B2)',
    skillsNotProven: 'Competencias no demostradas',
    toValidate: 'Por confirmar',
    removed: 'Elementos eliminados',
    recommendations: 'Recomendaciones',
    recommended: 'Recomendado',
    experienceN: 'Experiencia %{n}',
    recoAsk: 'Recomendación que pedir',
    recoAskWho: 'Recomendación que pedir (%{who})',
  },

  myLetter: {
    title: 'Mi carta de presentación',
    subtitle: 'Crea tu carta de presentación y luego analízala.',
    pitchLead: 'Una carta adaptada al puesto marca la diferencia.',
    arguments: {
      analysed: 'Analizada según el puesto buscado',
      skills: 'Pone en valor tus competencias',
      structure: 'Estructura convincente',
      ready: 'Lista para enviar',
    },
    createSection: 'Crear mi carta',
    createTitle: 'Crear una carta',
    createSubtitle: 'A partir de uno de tus CV',
    listSection: 'Mis cartas',
    listCount: 'Mis cartas · %{count}',
    seeAll: 'Ver todo',
    newLetter: 'Nueva',
    listSubtitle: 'Encuentra, vuelve a abrir y gestiona tus cartas',
    improveSection: 'Mejorar mi carta',
    analyseTitle: 'Analizar mi carta',
    analyseSubtitle: 'Puntuación, errores detectados y prioridades',
    lastScore: 'Última puntuación',
    lastScoreNone: 'Analiza una carta para descubrir su puntuación.',
    savedCount: {
      zero: 'Ninguna carta guardada',
      one: '%{count} carta guardada',
      other: '%{count} cartas guardadas',
    },
  },

  letterAnalyse: {
    importTitle: 'Importar mi carta',
    importSubtitle: 'PDF / Word',
    importPicked: 'Archivo importado: %{name}',
    importRemove: 'Quitar el archivo',
    orPaste: 'o pega su texto',
    rebuiltFromPdf: 'Texto reconstruido a partir del PDF importado.',
    title: 'Analizar mi carta',
    subtitle: 'La IA evalúa tu carta frente al puesto buscado y te dice qué corregir primero.',
    resultTitre: 'Análisis de mi carta',
    tabSaved: 'Mis cartas',
    tabImport: 'Importar',
    tabPaste: 'Pegar',
    importHint: 'PDF o Word. Se guardará en Mis cartas con su análisis.',
    pasteLabel: 'Texto de tu carta',
    pastedTitle: 'Carta pegada el %{date}',
    cvCrossedNone: 'Sin CV: los hechos de tu carta no se verificarán.',
    offerSub: 'Opcional, para un análisis más preciso',
    optionsTitre: 'Más opciones',
    optionsSousTitre: 'Perfil del candidato',
    profilRepris: 'Perfil tomado de tu cuenta.',
    sourceSection: 'LA CARTA QUE QUIERES ANALIZAR',
    pickSaved: 'Elegir una carta guardada',
    pickSavedEmpty: 'Aún no hay cartas guardadas',
    pasteTitle: 'Pegar una carta',
    pasteSubtitle: 'Una carta escrita en otro lugar',
    pastePlaceholder: 'Pega aquí el texto de tu carta…',
    pasteSaved: 'Tu carta pegada se guardará en «Mis cartas» junto con su análisis.',
    titleLabel: 'Título de la carta (opcional)',
    titlePlaceholder: 'ej. Alternancia – Responsable de comunicación',
    offerLabel: 'Anuncio',
    offerHint: 'Sin anuncio, el análisis se basa en el puesto buscado y en lo que espera el mercado.',
    offerPlaceholder: 'Pega el anuncio aquí…',
    profileLabel: 'Tu perfil',
    profileHint: 'Define el nivel de exigencia: no se juzga igual la carta de un escolar que la de un directivo.',
    metierLabel: 'Puesto buscado',
    metierPlaceholder: 'ej. Responsable de comunicación',
    submit: 'Analizar mi carta',
    loading: 'La IA está analizando tu carta…',
    empty: 'Añade la carta que quieres analizar.',
    failed: 'El análisis ha fallado. Inténtalo de nuevo.',
    cvCrossed: 'Análisis cruzado con tu CV principal.',
    cvNotCrossed: 'No hay ningún CV disponible: no se han verificado los datos que aparecen en la carta.',
    obsolete: 'Esta carta se ha modificado desde su análisis. La puntuación corresponde a la versión anterior.',
    reanalyse: 'Volver a analizar',
    improve: 'Mejorar mi carta',
    prioritiesSection: 'Corregir primero',
    errorsSection: 'Errores detectados',
    strengthsSection: 'Lo que ya funciona',
    detailSection: 'El detalle por criterio',
    coveredSection: 'REQUISITOS CUBIERTOS',
    missingSection: 'REQUISITOS NO TRATADOS',
    incoherenceSection: 'Diferencias con tu CV',
    faults: {
      zero: 'Ninguna falta detectada',
      one: '%{count} falta detectada',
      other: '%{count} faltas detectadas',
    },
    verdicts: {
      a_refaire: 'Hay que rehacerla',
      a_retravailler: 'Hay que trabajarla',
      envoyable_apres_corrections: 'Enviable tras correcciones',
      solide: 'Sólida',
      excellente: 'Excelente',
    },
    criteres: {
      C1: 'Adecuación al puesto',
      C2: 'Pruebas y concreción',
      C3: 'Personalización',
      C4: 'Estructura y extensión',
      C5: 'Lengua y estilo',
      C6: 'Protocolo y forma',
    },
    niveaux: {
      insuffisant: 'Insuficiente',
      fragile: 'Frágil',
      solide: 'Sólido',
      excellent: 'Excelente',
    },
    gravites: {
      majeure: 'Grave',
      moyenne: 'Media',
      mineure: 'Leve',
    },
  },

  // « Améliorer ma lettre » (1.2.0).
  coverLetterImprove: {
    title: 'Mejorar mi carta',
    blocked: 'No se pudo completar la mejora.',
    retry: 'Reintentar',
    reponseInvalide: 'La respuesta está incompleta. Vuelve a intentarlo en un momento.',
    saveFailed: 'No se pudo guardar.',
    copieTitre: '%{titre} (mejorada)',
    tags: { hook: 'Arranque', proof: 'Prueba', keywords: 'Palabras clave', clarity: 'Claridad', closing: 'Cierre', subject: 'Asunto' },
    modif: { objet: 'Asunto de la carta', paragraphe: 'Párrafo %{numero}', appliquer: 'Aplicar: %{nom}' },
    revue: {
      titre: {
        one: '%{count} cambio propuesto',
        other: '%{count} cambios propuestos',
      },
      aide: 'Acepta o rechaza cada cambio. Tu carta actual no cambia: tus decisiones generan una carta nueva, añadida a «Mis cartas».',
      longueur: 'Cuerpo de la carta: %{avant} palabras antes, %{apres} palabras después.',
    },
    pied: {
      retenues: 'Elegidos: %{count} de %{total}',
      appliquer: {
        one: 'Aplicar %{count} cambio',
        other: 'Aplicar %{count} cambios',
      },
      aucune: 'Ningún cambio elegido',
    },
    vide: { titre: 'Tu carta ya es sólida', detail: 'La IA no encontró ningún cambio útil que proponer. No se ha descontado nada de tu cuota.' },
    fait: { titre: 'Carta nueva guardada', detail: 'Tu carta mejorada está en «Mis cartas». Tu carta original no ha cambiado.', ouvrir: 'Abrir la carta' },
  },

  letterList: {
    open: 'Abrir',
    title: 'Mis cartas',
    subtitle: 'Encuentra, vuelve a abrir y gestiona tus cartas guardadas.',
    loadError: 'No se pudieron cargar tus cartas. Inténtalo de nuevo.',
    emptyTitle: 'Aún no tienes ninguna carta.',
    emptyText: 'Genera tu primera carta a partir de uno de tus CV y se guardará aquí.',
    emptyCta: 'Crear mi carta',
    deleteTitle: '¿Eliminar esta carta?',
    deleteBody: '«%{title}» se eliminará definitivamente.',
  },

  letterCreate: {
    erreurFormat: 'No se pudo redactar la carta. Inténtalo de nuevo: no se ha usado ningún crédito.',
    modifierAnnonce: 'Editar el anuncio',
    change: 'Cambiar',
    changeA11y: 'Cambiar de CV',
    optionsTitre: 'Más opciones',
    optionsSousTitre: 'Perfil, organización, destinatario, correo',
    reprisDuCompte: 'Perfil y tipo de candidatura tomados de tu cuenta. Lo que elijas aquí se recordará para tu próxima carta.',
    failed: 'No se pudo generar la carta. Inténtalo de nuevo.',
    annonceOr: 'o importa el archivo del anuncio',
    annonceFile: 'PDF / Word',
    annonceFilePicked: 'Anuncio importado: %{name}',
    annonceFileRemove: 'Quitar el archivo',
    title: 'Generar una carta',
    subtitle: 'Elige un CV, pega el anuncio y ajusta el tono.',
    resultTitle: 'Tu carta',
    resultSubtitle: 'Reléela, revisa lo que haga falta y luego descárgala.',
    noCvTitle: 'Primero necesitas un CV',
    noCvText:
      'Tu carta se basa en tu CV para sacar tus experiencias y tus pruebas. Crea o importa un CV y luego vuelve aquí.',
    goToCv: 'Ir a Mi CV',
    toConfirm: 'Revisar antes de enviar',
    emailBlock: 'Correo de acompañamiento',
    download: 'Descargar mi carta',
    cvUsed: 'CV utilizado',
    annonce: 'Anuncio objetivo',
    annoncePlaceholder: 'Pega el anuncio aquí…',
    profil: 'Perfil',
    type: 'Tipo de candidatura',
    ton: 'Tono de la carta',
    langue: 'Idioma de la carta',
    organisation: 'Organización destinataria',
    organisationPlaceholder: 'Ej. ACME',
    metier: 'Puesto buscado',
    metierPlaceholder: 'Ej. Responsable de proyecto',
    recipientName: 'Destinatario — nombre',
    recipientNamePlaceholder: 'Déjalo vacío si no lo conoces',
    recipientRole: 'Destinatario — cargo',
    recipientRolePlaceholder: 'Ej. Responsable de selección',
    city: 'Tu ciudad',
    cityPlaceholder: 'Ej. Lyon',
    addEmail: 'Añadir un correo de acompañamiento',
    generate: 'Generar mi carta',
    hint: 'Elige un CV y añade el anuncio para empezar la generación.',
    defaultTitle: 'Carta de presentación',
    saveFailed: 'Tu carta se ha generado, pero no se pudo guardar.',
    exportFailed: 'La exportación ha fallado.',
    wordCount: '%{count} palabras',
    profils: {
      collegien: 'Escolar (prácticas de secundaria)',
      lyceen: 'Estudiante de bachillerato',
      etudiant_jeune_diplome: 'Estudiante o recién titulado',
      actif_confirme: 'Profesional con experiencia',
      manager_cadre_dirigeant: 'Mánager o directivo',
      reconversion: 'Cambio de carrera',
    },
    types: {
      reponse_offre: 'Respuesta a una oferta',
      candidature_spontanee: 'Candidatura espontánea',
      mobilite_interne: 'Movilidad interna',
    },
    tons: {
      classique: 'Clásico',
      chaleureux: 'Cercano',
      direct: 'Directo',
      entrepreneurial: 'Emprendedor',
      institutionnel: 'Institucional',
      executif: 'Ejecutivo',
    },
    langues: { francais: 'Francés', anglais: 'Inglés' },
  },

  interview: {
    startFailed: 'No se pudo iniciar la entrevista. Inténtalo de nuevo.',
    noCv: 'Todavía no tienes un CV. Crea uno primero para iniciar una entrevista.',
    title: 'Entrevista IA',
    cvUsed: 'CV utilizado',
    change: 'Cambiar',
    changeA11y: 'Cambiar de CV',
    hintEmpty: 'Indica el puesto buscado para empezar la entrevista.',
    loadError: 'No se pudo cargar.',
    addCv: 'Añadir otro CV',
    posteSection: 'Puesto buscado',
    posteFile: 'Añadir una descripción del puesto',
    postePaste: 'Pegar una oferta de empleo',
    posteExample: 'Ej.: Jefe de marketing',
    pickFile: 'PDF / Word',
    pickCaptures: 'Capturas',
    offerPlaceholder: 'Pega aquí el texto de la oferta…',
    companyType: 'Tipo de empresa (opcional)',
    companyTypePlaceholder: 'startup, gran grupo, consultora, pyme…',
    adaptNote: 'La IA analiza el puesto y adapta las preguntas.',
    languageLabel: 'Idioma de la entrevista',
    start: 'Empezar la entrevista',
    interlocutors: {
      recruiter: {
        label: 'Reclutador',
        points: { motivation: 'Motivación', parcours: 'Trayectoria', competences: 'Competencias' },
      },
      manager: {
        label: 'Mánager',
        points: { terrain: 'Día a día', metiers: 'Oficio', situation: 'Caso práctico' },
      },
    },
    quitTitle: '¿Abandonar la entrevista?',
    quitBody: 'Perderás tu progreso.',
    quitCancel: 'Seguir con la entrevista',
    quitConfirm: 'Abandonar',
    muteVoice: 'Silenciar la voz del reclutador',
    voixActivee: 'Voz activada',
    downloadPdf: 'Descargar el PDF',
    pdfErreur: 'No se pudo crear el PDF. Inténtalo de nuevo.',
    voixCoupee: 'Voz silenciada',
    voixReviendra: {
      lundi: 'Voz del teléfono por ahora: la voz realista volverá el lunes.',
      mois: 'Voz del teléfono por ahora: la voz realista volverá el día 1 del mes.',
      entretien: 'Voz del teléfono hasta el final de esta entrevista: la voz realista volverá en la próxima.',
      bientot: 'Voz del teléfono por ahora: la voz realista volverá pronto.',
    },
    voixRestante: {
      semaine: 'Voz realista: te quedan %{n} entrevistas esta semana.',
      semaineUn: 'Voz realista: te queda 1 entrevista esta semana.',
      semaineAucun: 'Voz realista: se acabó por esta semana. La voz del teléfono toma el relevo hasta el lunes.',
      mois: 'Voz realista: te quedan %{n} entrevistas este mes.',
      moisUn: 'Voz realista: te queda 1 entrevista este mes.',
      moisAucun: 'Voz realista: se acabó por este mes. La voz del teléfono toma el relevo hasta el día 1.',
    },
    unmuteVoice: 'Activar la voz del reclutador',
    listen: 'Escuchar',
    finish: 'Terminar la entrevista',
    answerPlaceholder: 'Tu respuesta…',
    stopDictation: 'Detener el dictado',
    startDictation: 'Responder por voz',
    clearAnswer: 'Borrar',
    send: 'Enviar',
    strengths: 'Puntos fuertes',
    improvements: 'A mejorar',
    tips: 'Consejos',
    restart: 'Empezar de nuevo',
    phasePresentation: 'Fase de presentación',
    phaseOngoing: 'Entrevista en curso',
    feedbackHeader: 'Tu feedback',
    overallLabel: 'Valoración global',
    dureeMin: '%{n} min',
  },

  profile: {
    fallbackName: 'Tu perfil',
    personalInfo: 'Información personal',
    personalInfoSubtitle: 'Gestiona tu información y tus preferencias',
    subscription: 'Mi suscripción',
    subscriptionSubtitle: 'Gestiona tu plan y tus facturas',
    activity: 'MI ACTIVIDAD ESTE MES',
    coaching: 'MI COACHING',
    coachingHint: 'El horario se acuerda por correo.',
    bookSession: 'Reservar una sesión',
    feedbackTitle: '¿Tienes preguntas o mejoras que proponernos?',
    feedbackSubtitle: '¡Tu opinión cuenta!',
    avatarSheet: 'Foto o fondo del avatar',
    importFailed: 'No se pudo importar. Inténtalo de nuevo.',
    stats: {
      cvCreated: 'CV creados',
      interviews: 'Entrevistas IA',
      cvScores: 'Evaluaciones de CV',
      linkedinScores: 'Evaluaciones de LinkedIn',
    },
    noSession: 'Ninguna sesión programada',
    sessionFallback: 'Sesión',
    lastSession: 'Última sesión reservada el %{date} — %{formule}',
  },

  personalInfo: {
    firstName: 'Nombre',
    firstNamePlaceholder: 'Tu nombre',
    lastName: 'Apellidos',
    lastNamePlaceholder: 'Tus apellidos',
    email: 'Correo de contacto',
    emailPlaceholder: 'tu@ejemplo.com',
    phone: 'Teléfono',
    phonePlaceholder: '06 12 34 56 78',
    address: 'Dirección / ciudad',
    addressPlaceholder: 'Ej. París',
    linkedin: 'URL de LinkedIn',
    linkedinPlaceholder: 'https://www.linkedin.com/in/…',
  },

  profileCompletion: {
    title: 'Una cosa más',
    birthDatePlaceholder: 'DD/MM/AAAA',
    continue: 'Continuar',
    later: 'Más tarde',
    saveFailed: 'No se pudo guardar.',
  },

  notifications_screen: {
    title: 'Notificaciones',
    empty: 'No hay notificaciones nuevas por ahora.',
    settingsSubtitle: 'Tus recordatorios y consejos para avanzar en tu búsqueda.',
    tips: 'CONSEJOS',
  },

  auth: {
    signup: {
      title: 'Crea tu cuenta,',
      titleAccent: 'lánzate.',
      subtitle: 'Mejora tu CV, prepara tus entrevistas y avanza hacia tu contrato.',
      cta: 'Crear mi cuenta',
    },
    signupEtablissement: {
      title: 'Cree su cuenta de',
      titleAccent: 'centro',
      subtitle: 'y ofrezca a sus estudiantes una herramienta para preparar sus candidaturas.',
    },
    signupPartenaire: {
      title: 'Cree su cuenta de',
      titleAccent: 'socio',
      subtitle: 'y ofrezca a los jóvenes a los que acompaña una herramienta para preparar sus candidaturas.',
    },
    signin: {
      title: 'Sigue',
      titleAccent: 'destacando.',
      subtitle: 'Retoma tus mejoras y avanza hacia tu próxima entrevista.',
      cta: 'Iniciar sesión',
    },
    pending:
      'Si esta dirección no está ya en uso, recibirás un correo de confirmación. Revisa también tu carpeta de spam.',
    invalidEmail: 'Dirección de correo no válida.',
    invalidPassword: 'Contraseña no válida.',
    emailLabel: 'Correo electrónico',
    emailPlaceholder: 'nombre@correo.es',
    passwordLabel: 'Contraseña',
    hidePassword: 'Ocultar la contraseña',
    showPassword: 'Mostrar la contraseña',
    tabSignin: 'Iniciar sesión',
    tabSignup: 'Crear cuenta',
    forgot: {
      link: '¿Olvidaste tu contraseña?',
      title: 'Recupera el',
      titleAccent: 'acceso.',
      subtitle: 'Indica tu correo y te enviamos un enlace para elegir una contraseña nueva.',
      cta: 'Enviar el enlace',
      sent: 'Si existe una cuenta con esta dirección, te hemos enviado un correo con un enlace para elegir una contraseña nueva. Revisa tu carpeta de spam y abre el enlace desde este teléfono.',
      back: 'Volver a iniciar sesión',
    },
    newPassword: {
      title: 'Elige tu nueva',
      titleAccent: 'contraseña.',
      subtitle: 'Sustituye a la anterior.',
      newLabel: 'Contraseña nueva',
      confirmLabel: 'Confirma la contraseña',
      tooShort: 'La contraseña debe tener al menos %{min} caracteres.',
      mismatch: 'Las dos contraseñas no coinciden.',
      cta: 'Guardar',
      cancel: 'Cancelar y cerrar sesión',
    },
    or: 'o',
    google: 'Continuar con Google',

    error: {
      invalidCredentials: 'Correo o contraseña incorrectos.',
      emailNotConfirmed:
        'Tu cuenta aún no está confirmada. Abre el enlace que te enviamos por correo — revisa también tu carpeta de spam.',
      emailInvalid: 'Esta dirección de correo no es válida.',
      validationFailed: 'Revisa tu correo y tu contraseña.',
      weakPassword: 'Contraseña demasiado débil. Elige una más larga, con números y letras.',
      rateLimit: 'Demasiados intentos. Inténtalo de nuevo en unos minutos.',
      userBanned: 'Esta cuenta está suspendida. Escríbenos para saber más.',
      network: 'No se pudo conectar. Revisa tu conexión a internet e inténtalo de nuevo.',
      confirmExpired:
        'Este enlace de confirmación ya no es válido. Vuelve a crear tu cuenta con la misma dirección para recibir uno nuevo.',
      confirmWrongDevice: 'Abre este enlace desde el teléfono en el que creaste tu cuenta.',
      resetExpired: 'Este enlace de restablecimiento ya no es válido. Pide uno nuevo con «¿Olvidaste tu contraseña?».',
      resetWrongDevice: 'Abre el último correo recibido, desde el teléfono en el que pediste el restablecimiento.',
      samePassword: 'Elige una contraseña distinta de la anterior.',

      serverSignup:
        'No podemos crear tu cuenta en este momento — el problema es nuestro, no tuyo. Inténtalo de nuevo en unos minutos o continúa con %{providers}.',
      serverSignin:
        'No podemos iniciar tu sesión en este momento — el problema es nuestro, no tuyo. Inténtalo de nuevo en unos minutos o continúa con %{providers}.',
      serverOauth:
        'No se pudo iniciar sesión — el problema es nuestro, no tuyo. Inténtalo de nuevo en unos minutos o inicia sesión con tu correo y tu contraseña.',
      popupBlocked:
        'Tu navegador ha bloqueado la ventana de inicio de sesión de Google. Permite las ventanas emergentes para este sitio y vuelve a iniciar sesión — o inicia sesión con tu correo y tu contraseña.',
      appleUnknown:
        'No se pudo iniciar sesión con Apple. Comprueba que has iniciado sesión en iCloud en este dispositivo y que tu cuenta de Apple tiene la autenticación de doble factor. Si no, inicia sesión con tu correo y tu contraseña.',
      serverSignout: 'No se pudo cerrar la sesión. Inténtalo de nuevo en unos minutos.',
      serverConfirm:
        'No podemos confirmar tu cuenta en este momento — el problema es nuestro, no tuyo. Inténtalo de nuevo en unos minutos o continúa con %{providers}.',
      serverReset:
        'No podemos restablecer tu contraseña en este momento — el problema es nuestro, no tuyo. Inténtalo de nuevo en unos minutos.',

      googleStart: 'No podemos abrir el inicio de sesión con Google. Inténtalo de nuevo en unos minutos.',
      googleResponse: 'La respuesta de Google está incompleta. Inténtalo de nuevo.',
      appleResponse: 'La respuesta de Apple está incompleta. Inténtalo de nuevo.',
      deleteFailed:
        'Tu cuenta no se ha eliminado: la operación falló antes de borrar nada, tus datos están intactos. Inténtalo de nuevo en unos minutos.',
      deleteAppleCancelled: 'Eliminación cancelada: confirma con Apple para eliminar tu cuenta.',

      providersIos: 'Google o Apple',
      providersAndroid: 'Google',
    },
  },

  onboarding: {
    highlights: { cv: 'CV', linkedin: 'LinkedIn', interview: 'Entrevistas', coach: 'Coach' },
    title: 'Tu perfil merece',
    mots: {
      reponses: 'respuestas.',
      cv: 'un CV que pase.',
      entretien: 'una entrevista.',
      contrat: 'un contrato.',
    },
    sousTitre: 'Te acompañamos desde el CV hasta la firma de tu contrato.',
    wheel: {
      cv: 'CV ATS',
      linkedin: 'LinkedIn',
      interview: 'Entrevistas IA',
      coach: 'Coach experto',
      offers: 'Ofertas',
      letter: 'Carta',
      center: 'Tu contrato',
    },
    quickSignup: 'Registro rápido',
    dataProtected: 'Datos protegidos',
    createAccount: 'Crear mi cuenta',
    haveAccount: 'Ya tengo una cuenta',
  },


  acceptTerms: {
    titleOutdated: 'Nuestras condiciones han cambiado',
    titleFirst: 'Ya casi está ✨',
    changedPrefix: 'Nuestras ',
    changedTermsLink: 'condiciones de uso',
    changedMiddle: ' y nuestra ',
    changedPrivacyLink: 'política de privacidad',
    changedSuffix:
      ' se han actualizado (versión %{version}). Léelas y acéptalas para seguir usando la aplicación.',
    firstPrefix: 'Hemos simplificado nuestras condiciones para ',
    firstAccent: 'proteger mejor tus datos',
    pillars: {
      data: 'Datos\nprotegidos',
      transparency: 'Transparencia\ntotal',
      consent: 'Ninguna acción\nsin ti',
    },
    docTerms: 'Condiciones de uso',
    docPrivacy: 'Política de privacidad',
    accept: 'Continuar',
    refuseOutdated: 'Rechazar y cerrar sesión',
    refuseFirst: 'Más tarde, cerrar sesión',
    help: '¿Necesitas ayuda?',
    deleteAccount: 'Eliminar mi cuenta',
    saveFailed: 'No se pudo guardar. Inténtalo de nuevo.',
  },

  legalHeaders: {
    documentsEnFrancais: 'Documentos disponibles en francés',
    privacy: 'Privacidad y datos',
    terms: 'Condiciones de uso',
    dataDeletion: 'Eliminación de la cuenta',
  },

  plans: {
    free: {
      tagline: 'Para empezar',
      price: 'Gratis',
      period: 'para siempre',
      entretiens: '1 entrevista IA / mes, con la voz natural del reclutador',
      features: [
        '1 evaluación y 1 importación de CV / mes',
        '1 auditoría de LinkedIn / mes',
        '1 carta y 1 análisis de carta / mes',
        '1 exportación PDF / mes',
        'Sin creación de CV con IA',
        'Plantillas básicas',
      ],
      featuresOffres: ['Ofertas de alternancia y empleo: acceso libre', '2 candidaturas con IA / mes (pestaña Ofertas)'],
    },
    plus: {
      tagline: 'El equilibrio justo',
      period: '/ mes',
      entretiens: '8 entrevistas IA / mes',
      voix: '2 entrevistas por semana con la voz realista del reclutador',
      features: [
        '5 creaciones y 5 importaciones de CV / mes',
        '5 evaluaciones de CV / mes',
        '5 auditorías de LinkedIn / mes',
        '5 cartas y 5 análisis de carta / mes',
        '20 reescrituras IA de secciones / mes',
        'Exportación ilimitada y todas las plantillas',
      ],
      featuresOffres: ['15 candidaturas con IA / mes (pestaña Ofertas)'],
    },
    pro: {
      tagline: 'Para llegar hasta el final de tu búsqueda',
      period: '/ mes',
      entretiens: '20 entrevistas IA / mes',
      voix: '5 entrevistas por semana con la voz realista del reclutador',
      ecart: '20 entrevistas y 60 candidaturas con IA al mes',
      ecartSansOffres: '20 entrevistas IA al mes',
      features: [
        'Ilimitado: CV, evaluaciones, auditorías de LinkedIn, cartas y análisis',
        'Reescrituras IA y exportaciones ilimitadas',
        'Plantillas premium',
      ],
      featuresOffres: ['60 candidaturas con IA / mes (pestaña Ofertas)'],
    },
    school: {
      features: [
        'Todas las funciones, para todos los alumnos',
        'Ideal para escuelas y CFA (centros de formación de aprendices)',
        'Facturación por usuario',
        'Licencia',
        'Panel de seguimiento del centro',
        'Informe trimestral',
        'Personalización',
      ],
    },
  },

  plansExtra: {
    proAnnual: {
      name: 'Pro anual',
      period: '/año',
      badge: '−%{pourcentage} %',
      equivalent: 'es decir, %{monthly}/mes × 12',
      cta: 'Elegir el anual',
    },
  },

  paywall: {
    mentionAbonnement: 'Suscripción mensual o anual según el plan elegido, renovada automáticamente por el mismo periodo salvo cancelación al menos 24 horas antes del final del periodo en curso. Puedes cancelarla en cualquier momento en la gestión de suscripciones de App Store o Google Play; la cancelación surte efecto al final del periodo en curso.',
    tonPlan: 'Tu plan',
    bientotSurPlateforme: 'Las suscripciones llegarán pronto a esta plataforma.',
    currentPlan: 'Tu plan actual: ',
    title: 'Sube de nivel',
    capReached: 'Ya no te quedan usos este mes. Tu contador se reinicia el %{date}.',
    notIncluded: 'Esta función no está incluida en tu plan actual.',
    tabIndividual: 'Individual',
    tabSchools: 'Escuelas',
    restore: 'Restaurar mis compras',
    contact: 'Contáctanos',
    recommended: 'Recomendado',
    subscribe: 'Suscribirme',
    pendingApproval: 'Compra pendiente de aprobación.',
    prixIndisponibles: 'Los precios no están disponibles ahora. Inténtalo de nuevo en un momento.',
    genericError: 'Se ha producido un error.',
    activated: 'Suscripción activada 🎉',
    restored: 'Compras restauradas.',
    activating: 'Compra recibida. Activando tu suscripción…',
    activationEnCours: 'Compra recibida. Tu suscripción se está activando: puede tardar un momento.',
    restoreEnCours: 'Restauración enviada. Si hay una suscripción vinculada a tu cuenta, aparecerá en un momento.',
    schoolMailSubject: 'Offre établissement Horméia',
  },

  coaching: {
    tarifsIndisponibles: 'Tarifas no disponibles por el momento.',
    reessayer: 'Reintentar',
    montantIncoherent:
      'El pago no está disponible por el momento: el precio mostrado no coincide con el importe que se cobraría. Preferimos no cobrar nada hasta que se resuelva.',
    retourSuccesTitre: 'Pago confirmado',
    retourSuccesCorps:
      'Hemos recibido tu pago. Recibirás un correo de confirmación con los detalles de tu sesión.',
    retourEnCoursTitre: 'Pago en proceso',
    retourEnCoursCorps:
      'Tu banco aún no ha confirmado el pago, algo normal con algunos medios de pago. No lo repitas: se te cobraría dos veces. Recibirás un correo en cuanto se confirme.',
    retourEchecTitre: 'Pago rechazado',
    retourEchecCorps:
      'El pago no se completó y no se te ha cobrado nada. Puedes intentarlo de nuevo, con el mismo medio de pago u otro.',
    retourAnnuleTitre: 'Pago cancelado',
    retourAnnuleCorps: 'Interrumpiste el pago y no se te ha cobrado nada. Tu horario sigue disponible.',
    retourVersCoaching: 'Volver al coaching',
    title: 'Coaching',
    pickSlot: 'Elegir mi horario',
    soon: 'Próximamente',
    slotBeforePayment: 'Tu horario se confirma antes de cualquier pago.',
    howTitle: 'Cómo funciona',
    step1: 'Eliges tu horario',
    step2: 'Validas tu reserva',
    step3: 'Pagas tu sesión',
    pickFormule: 'Elige tu modalidad',
    formuleNotFound: 'Modalidad no encontrada. Vuelve atrás y elige una modalidad.',
    reserve: 'Reservar',
    checkingSlot: 'Estamos comprobando tu horario en la agenda…',
    slotConfirmed: 'Tu horario está confirmado. Solo falta validar el pago.',
    chosenFormule: 'MODALIDAD ELEGIDA',
    email: 'CORREO',
    searching: 'LO QUE BUSCAS (OPCIONAL)',
      payUnavailable:
        'El pago no está disponible por el momento. Inténtalo de nuevo más tarde.',
    paid: 'Pago realizado ✓. Tu reserva está confirmada, Julien se pondrá en contacto contigo.',
    processing: 'Tu pago se está procesando. No pagues una segunda vez: recibirás una confirmación en cuanto se valide.',
    securePayment: 'Pago seguro con tarjeta (Stripe). Solo se te cobra una vez.',
    nameRequired: 'Indica tu nombre.',
    invalidEmail: 'Dirección de correo no válida.',
    paymentFailed: 'No se pudo realizar el pago. Inténtalo de nuevo.',
    paymentFailedShort: 'No se pudo realizar el pago.',
  },

  cvCreateFlow: {
    titre: 'Crear un CV',
    etapeSur: '%{n} de %{total}',
    step1Titre: '¿A qué puesto aspiras?',
    step1Sous: 'La IA adapta todo el CV a este puesto.',
    step2Titre: 'Tu trayectoria',
    step2Sous: 'No hace falta redactar bien: la IA reformula por ti.',
    step3Titre: 'Para terminar',
    step3Sous: 'Todo es opcional, puedes generar tu CV ahora.',
    nameFromProfile: 'Desde tu perfil',
    importTitre: '¿Ya tienes un CV?',
    importSous: 'Impórtalo, es más rápido.',
    roleRequired: 'Indica el puesto buscado para continuar.',
    tuileProjet: 'Un proyecto',
    tuileAutre: 'Otra cosa',
    optionnel: 'Opcional',
    ajoute: 'Añadido',
    nameLabel: 'Nombre en el CV',
    namePlaceholder: 'Ej. Jean Dupont',
    languageLabel: 'Idioma del CV',
    languageHint: 'Puedes responder en francés y obtener un CV en inglés, o al revés.',
    contratLabel: 'Buscas…',
    contratHint: 'Opcional. Vuelve a tocar para quitarlo.',
    contrats: { alternance: 'Alternancia', stage: 'Prácticas', cdi: 'CDI (indefinido)', cdd: 'CDD (temporal)' },
    generate: 'Generar mi CV',
    next: 'Continuar',
    buildingTitle: 'Estamos creando tu CV.',
    buildingHint: 'Unos segundos, prometido.',
    editTitle: 'Editar mi CV',
    cancel: 'Cancelar',
    save: 'Guardar',
    readyTitle: 'Tu CV está listo',
    atsScore: 'Puntuación ATS',
    edit: 'Editar',
    saving: 'Guardando…',
    retrySave: 'Reintentar el guardado',
    download: 'Exportar mi CV',
    condensePourUnePage: 'Contenido condensado para que quepa en una página',
    aiAdvice: 'CONSEJOS DE LA IA',
    targetOffer: 'APUNTAR A UNA OFERTA (OPCIONAL)',
    offerPlaceholder: 'Pega la oferta aquí…',
    offerFile: 'Oferta (PDF/Word)',
    offerCaptures: 'Capturas',
    recommendCta: 'Recomendaciones para esta oferta',
    recommendSection: 'RECOMENDACIONES PARA ESTA OFERTA',
    testInScore: 'Probar este CV en Evaluar mi CV',
    skillPlaceholder: 'Ej. SQL',
    interestPlaceholder: 'Ej. Trail',
    noterCe: 'Evaluar este CV',
    savedIn: 'Guardado en Mis CV',
    editsSaved: 'Cambios guardados',
    saveFailed: 'Tu CV se ha generado pero no se pudo guardar.',
    editSaveFailed: 'No se pudo guardar.',
    offerHint: 'Añade una oferta de empleo: la IA te sugiere cómo valorar tu trayectoria para esta oferta (sin inventar nada). Tú decides aplicarlo desde la edición.',
  },

  help: {
    title: 'Ayuda y contacto',
    faqSection: 'PREGUNTAS FRECUENTES',
    contactSection: 'CONTÁCTANOS',
    writeUs: 'Escríbenos',
    sendEmail: 'Enviar un correo',
    mailSubject: 'Aide — Horméia',
    faq: {
      ats: {
        q: '¿Cómo se calcula mi puntuación ATS?',
        a: 'La IA analiza tu CV según los criterios que priorizan los programas de selección de candidaturas (ATS): presencia de las palabras clave del puesto, estructura clara, secciones estándar y resultados cuantificados. La puntuación refleja esa compatibilidad e incluye consejos concretos para mejorarla.',
      },
      privacy: {
        q: '¿Mis datos son confidenciales?',
        a: 'Tus CV y tu información sirven para que funcionen las funciones de la app (evaluación, creación, entrevista). Pronto publicaremos una página dedicada a la privacidad y a la gestión de tus datos; mientras tanto, escríbenos para cualquier pregunta.',
      },
      coaching: {
        q: '¿Cómo funciona el coaching?',
        a: 'El coaching es un acompañamiento humano con Julien Dauzats, experto en RR. HH. — independiente de las funciones de IA. Eliges una modalidad, reservas desde la app y la sesión se hace por videollamada.',
      },
    },
  },

  misc: {
    personalInfoTitle: 'Información personal',
    save: 'Guardar',
    coachNamePlaceholder: 'Tu nombre',
    coachGoalPlaceholder: 'Ej. preparar una entrevista de data la semana que viene',
    atsCompatible: 'Compatible con ATS',
    completeProfile: 'Completa tu perfil',
    completeProfileCta: 'Completar mi perfil',
    later: 'Más tarde',
    feedbackQuestion: '¿Te ha sido útil este resultado?',
    send: 'Enviar',
    importPhoto: 'Importar una foto',
    orColour: 'O UN FONDO DE COLOR',
    add: 'Añadir',
    yearPlaceholder: 'Año',
    comingSoon: 'Próximamente.',
  },

  progress: {
    linkedinKitSections: [
      'Redacto tu titular…',
      'Redacto tu sección Acerca de…',
      'Reformulo tus experiencias…',
      'Elijo tus competencias…',
      'Ajusto tus parámetros de búsqueda…',
      'Preparo tus consejos exprés…',
      'Termino tu kit…',
    ],

    linkedinSections: [
      'Leo tu perfil…',
      'Detecto los errores habituales…',
      'Preparo tu puntuación y tus prioridades…',
    ],

    elapsed: {
      start: 'Estamos creando tu CV…',
      mid: 'Unos segundos más…',
      long: 'Está tardando un poco más de lo habitual, seguimos.',
    },
    letter: {
      start: 'Estamos redactando tu carta…',
      mid: 'Unos segundos más…',
      long: 'Está tardando un poco más de lo habitual, seguimos.',
    },
    linkedin: {
      start: 'Estamos analizando tu perfil…',
      mid: 'Unos segundos más…',
      long: 'Está tardando un poco más de lo habitual, seguimos.',
    },
    improve: {
      start: 'Estamos optimizando tu CV…',
      mid: 'Unos segundos más…',
      long: 'Está tardando un poco más de lo habitual, seguimos.',
    },
    improveLetter: {
      start: 'Estamos mejorando tu carta…',
      mid: 'Unos segundos más…',
      long: 'Está tardando un poco más de lo habitual, seguimos.',
    },
    received: 'Recibido',
    saved: 'Guardado',
  },

  // Licence accordée par l'établissement de l'étudiant (lot 4 du back-office). Aucun lien d'achat ici.
  licenceEcole: {
    title: 'Licencia de la escuela',
    loading: 'Cargando…',
    loadError: 'No se pudo cargar tu licencia. Inténtalo de nuevo.',
    retry: 'Reintentar',
    intro: '¿Tu escuela te ha enviado un código de invitación? Introdúcelo para activar tu licencia.',
    codeLabel: 'Código de invitación',
    codePlaceholder: 'XXXX-XXXX-XXXX',
    activer: 'Activar mi licencia',
    activating: 'Activando…',
    voitTitre: 'Lo que ve tu escuela',
    voit: [
      'Tu situación (en búsqueda, con contrato, contrato roto) y tus fechas clave, introducidas por tu escuela.',
      'Las acciones de acompañamiento que ha realizado por ti: llamadas, reuniones, ofertas enviadas.',
      'Tu actividad en la app: los días en que la usas. Es visible desde que activas tu licencia, porque te la proporciona tu escuela.',
    ],
    neVoitPas: 'Nunca ve el contenido de tus CV, cartas o entrevistas.',
    active: 'Licencia activa',
    via: 'Proporcionada por %{nom}',
    employeurTitre: 'Tu empresa',
    employeurContrat: 'Tu escuela te ha vinculado a %{nom} desde el %{date}.',
    employeurVoitTitre: 'Lo que vería tu empresa si lo autorizas',
    employeurVoit: [
      'Tu nombre, tu formación, tu escuela y las fechas de tu contrato.',
      'Tu situación (en búsqueda, con contrato, contrato roto).',
    ],
    employeurNeVoitPas: 'Nunca ve tu correo electrónico, el registro de acompañamiento de tu escuela, tus candidaturas, tu actividad en la app ni el contenido de tus CV, cartas o entrevistas.',
    employeurConsentTitre: 'Compartir mi situación con mi empresa',
    employeurConsentBody: 'Desactivado por defecto. Puedes retirarlo en cualquier momento: tu contrato y tu licencia no cambian. Tu escuela solo ve si has aceptado o no.',
    employeurConsentError: 'No se pudo guardar tu elección. Inténtalo de nuevo.',
    succes: 'Tu licencia de %{nom} está activada.',
    erreurs: {
      code_invalide: 'Este código no es válido. Compruébalo o pide uno nuevo a tu escuela.',
      invitation_expiree: 'Esta invitación ha caducado. Pide una nueva a tu escuela.',
      abonnement_actif: 'Ya tienes una suscripción activa. Cancélala en los ajustes de App Store o Google Play y vuelve a intentarlo.',
      plus_de_sieges: 'Tu escuela no tiene plazas disponibles. Contáctala.',
      organisation_inactive: 'La licencia de tu escuela no está activa por el momento. Contáctala.',
      inconnu: 'No se pudo activar ahora. Inténtalo de nuevo.',
    },
  },

  settings: {
    title: 'Ajustes',
    sections: {
      account: 'Cuenta',
      preferences: 'Preferencias',
      admin: 'Administración',
      danger: 'Zona de peligro',
    },
    privacy: 'Privacidad y datos',
    licenceEcole: 'Licencia de la escuela',
    dataDeletion: 'Eliminación de la cuenta y de los datos',
    notifications: 'Notificaciones',
    adminDashboard: 'Panel de administración',
    signOut: 'Cerrar sesión',
    deleteAccount: 'Eliminar mi cuenta',
    deleting: 'Eliminando…',
    deleteFailedTitle: 'No se pudo eliminar',
    deleteConfirmTitle: '¿Eliminar tu cuenta?',
    deleteConfirmBody: 'Tu cuenta se desactivará de inmediato y tus datos se eliminarán definitivamente al cabo de 29 días. Tu suscripción no se cancela automáticamente: cancélala en los ajustes del App Store o de Google Play.',
    language: {
      label: 'Idioma de la aplicación',
      hint: 'Los documentos que generas mantienen su propio idioma, elegido al crearlos.',
      relanceTitre: '¿Reiniciar la aplicación?',
      relanceTexte: 'Para cambiar a este idioma, Horméia tiene que reiniciarse. Tus CV, cartas y ajustes guardados se conservan; una entrevista o un texto sin guardar en curso se perderán.',
      relanceOk: 'Reiniciar',
    },
  },
  update: {
    banner: {
      title: 'Hay una nueva versión disponible',
      body: 'Actualiza Horméia para disfrutar de las últimas mejoras.',
      action: 'Actualizar',
      dismiss: 'Más tarde',
      dismissA11y: 'Ocultar esta invitación a actualizar',
    },
    blocking: {
      title: 'Esta versión ya no es compatible',
      body: 'Horméia ha evolucionado desde que se instaló esta versión, que ya no puede funcionar correctamente con el servicio. No es una avería, y no hay nada que arreglar por tu parte.',
      reassurance: 'Tus datos y tus documentos están intactos: los encontrarás en cuanto abras la nueva versión.',
      action: 'Instalar la actualización',
    },
  },
  quota: {
    resetDate: '1 de %{monthYear}',
    capReachedNoUpgrade:
      'Has alcanzado el límite de tu plan para esta función. Tu contador se reinicia el %{date}.',
    notIncludedNoUpgrade: 'Esta función no está incluida en tu licencia.',
  },
  errors: {
    network: 'No se pudo conectar. Revisa tu conexión y vuelve a intentarlo.',
    server: 'El servicio no está disponible por el momento. Inténtalo de nuevo en un instante.',
    aiBadOutput: 'La respuesta de la IA no se pudo aprovechar. Vuelve a lanzar el análisis.',
    profilTropEloigne: 'Esta oferta está demasiado lejos de tu CV para escribir una carta creíble.',
    invalidRequest: 'Revisa la información introducida y vuelve a intentarlo.',
    ficheTropLongue: 'La descripción del puesto es demasiado larga: 20 páginas o 10 000 caracteres como máximo.',
    unauthorized: 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
  },

  nouveautes: {
    titre: 'Novedades de Horméia',
    offresTitre: 'Las ofertas de alternancia',
    offresTexte: 'Encuentra ofertas de alternancia y de empleo que encajen con tu perfil y prepara tu candidatura con la IA.',
    voirOffres: 'Ver las ofertas',
    voixTitre: 'La voz del reclutador',
    voixTexte: 'Las simulaciones de entrevista ahora tienen una voz realista. Según tu plan: 1 entrevista al mes en Free, 2 por semana en Accélérateur o École y 5 por semana en Pro.',
    languesTitre: 'Nuevos idiomas',
    languesTexte: 'Horméia ya está disponible en español, portugués, chino y árabe. Puedes cambiar el idioma en Ajustes.',
    politiqueTitre: 'Política de privacidad',
    politiqueTexte: 'Hemos actualizado nuestra política de privacidad para describir la medición del uso de la aplicación. Puedes consultarla en cualquier momento en Ajustes.',
    voirPolitique: 'Leer la política',
    bouton: '¡Vamos!',
  },
};
